/**
 * Global enhancements for antd Image preview:
 * - Close button anchored to the image top-right (falls back to viewport when off-screen)
 * - Keeps zoomed/panned images within the viewport
 */

const CLOSE_BTN_MARGIN = 8;
const TRACK_INTERVAL_MS = 32;

type ParsedTransform = {
  x: number;
  y: number;
  scale: number;
  rotate: number;
  flipX: boolean;
  flipY: boolean;
};

function parseTransform(el: HTMLElement): ParsedTransform {
  const style = el.style.transform || '';
  const translateMatch = style.match(/translate3d\(\s*([-\d.]+)px,\s*([-\d.]+)px/);
  const scaleMatch = style.match(/scale3d\(\s*(-?[\d.]+),\s*(-?[\d.]+)/);
  const rotateMatch = style.match(/rotate\(\s*([-\d.]+)deg/);
  const scaleX = parseFloat(scaleMatch?.[1] || '1');
  const scaleY = parseFloat(scaleMatch?.[2] || '1');

  return {
    x: parseFloat(translateMatch?.[1] || '0'),
    y: parseFloat(translateMatch?.[2] || '0'),
    scale: Math.abs(scaleX) || 1,
    rotate: parseFloat(rotateMatch?.[1] || '0'),
    flipX: scaleX < 0,
    flipY: scaleY < 0
  };
}

function applyTransform(el: HTMLElement, t: ParsedTransform) {
  const scaleX = t.flipX ? -t.scale : t.scale;
  const scaleY = t.flipY ? -t.scale : t.scale;
  el.style.transform = `translate3d(${t.x}px, ${t.y}px, 0) scale3d(${scaleX}, ${scaleY}, 1) rotate(${t.rotate}deg)`;
}

/** Port of rc-image getFixScaleEleTransPosition — keeps pan within viewport bounds. */
function getFixScaleEleTransPosition(
  width: number,
  height: number,
  left: number,
  top: number
): { x?: number; y?: number } | null {
  const clientWidth = window.innerWidth;
  const clientHeight = window.innerHeight;

  const fixPoint = (key: 'x' | 'y', start: number, size: number, clientSize: number) => {
    const startAddSize = start + size;
    const offsetStart = (size - clientSize) / 2;
    if (size > clientSize) {
      if (start > 0) return { [key]: offsetStart };
      if (start < 0 && startAddSize < clientSize) return { [key]: -offsetStart };
    } else if (start < 0 || startAddSize > clientSize) {
      return { [key]: start < 0 ? offsetStart : -offsetStart };
    }
    return {};
  };

  if (width <= clientWidth && height <= clientHeight) {
    return { x: 0, y: 0 };
  }
  if (width > clientWidth || height > clientHeight) {
    return {
      ...fixPoint('x', left, width, clientWidth),
      ...fixPoint('y', top, height, clientHeight)
    };
  }
  return null;
}

function clampPreviewImage() {
  const img = document.querySelector('.ant-image-preview-img') as HTMLElement | null;
  if (!img) return;

  const parsed = parseTransform(img);
  const rect = img.getBoundingClientRect();
  const isRotated = parsed.rotate % 180 !== 0;
  const scaledWidth = img.offsetWidth * parsed.scale;
  const scaledHeight = img.offsetHeight * parsed.scale;

  const fixState = getFixScaleEleTransPosition(
    isRotated ? scaledHeight : scaledWidth,
    isRotated ? scaledWidth : scaledHeight,
    rect.left,
    rect.top
  );

  if (!fixState) return;

  const nextX = fixState.x !== undefined ? fixState.x : parsed.x;
  const nextY = fixState.y !== undefined ? fixState.y : parsed.y;

  if (nextX !== parsed.x || nextY !== parsed.y) {
    applyTransform(img, { ...parsed, x: nextX, y: nextY });
  }
}

function setCloseBtnPosition(closeBtn: HTMLElement, top: number, right: number) {
  closeBtn.style.setProperty('position', 'fixed', 'important');
  closeBtn.style.setProperty('display', 'flex', 'important');
  closeBtn.style.setProperty('visibility', 'visible', 'important');
  closeBtn.style.setProperty('opacity', '1', 'important');
  closeBtn.style.setProperty('pointer-events', 'auto', 'important');
  closeBtn.style.setProperty('z-index', '100001', 'important');
  closeBtn.style.setProperty('transform', 'none', 'important');
  closeBtn.style.setProperty('bottom', 'auto', 'important');
  closeBtn.style.setProperty('left', 'auto', 'important');
  closeBtn.style.setProperty('margin', '0', 'important');
  closeBtn.style.setProperty('top', `${top}px`, 'important');
  closeBtn.style.setProperty('right', `${right}px`, 'important');
}

function updateCloseButtonPosition() {
  const closeBtn = document.querySelector('.ant-image-preview-close') as HTMLElement | null;
  if (!closeBtn) return;

  // CSSMotion on the operations wrapper adds transform, which breaks position:fixed on children.
  const opsWrapper = document.querySelector(
    '.ant-image-preview-operations-wrapper'
  ) as HTMLElement | null;
  if (opsWrapper) {
    opsWrapper.style.setProperty('transform', 'none', 'important');
  }

  const img = document.querySelector('.ant-image-preview-img') as HTMLElement | null;
  const btnH = closeBtn.offsetHeight || 32;
  const btnW = closeBtn.offsetWidth || 32;
  const viewportTopRight = { top: CLOSE_BTN_MARGIN, right: CLOSE_BTN_MARGIN };

  if (!img) {
    setCloseBtnPosition(closeBtn, viewportTopRight.top, viewportTopRight.right);
    return;
  }

  const imgRect = img.getBoundingClientRect();
  const imgReady = imgRect.width > 10 && imgRect.height > 10;

  if (!imgReady) {
    setCloseBtnPosition(closeBtn, viewportTopRight.top, viewportTopRight.right);
    return;
  }

  const imageInView =
    imgRect.bottom > 0 &&
    imgRect.top < window.innerHeight &&
    imgRect.right > 0 &&
    imgRect.left < window.innerWidth;

  if (!imageInView) {
    setCloseBtnPosition(closeBtn, viewportTopRight.top, viewportTopRight.right);
    return;
  }

  // Anchor to image top-right: inset slightly inside the corner.
  const top = Math.max(
    CLOSE_BTN_MARGIN,
    Math.min(window.innerHeight - btnH - CLOSE_BTN_MARGIN, imgRect.top + CLOSE_BTN_MARGIN)
  );
  const right = Math.max(
    CLOSE_BTN_MARGIN,
    Math.min(
      window.innerWidth - btnW - CLOSE_BTN_MARGIN,
      window.innerWidth - imgRect.right + CLOSE_BTN_MARGIN
    )
  );

  setCloseBtnPosition(closeBtn, top, right);
}

function updatePreviewEnhancements() {
  updateCloseButtonPosition();
  clampPreviewImage();
}

let intervalId: ReturnType<typeof setInterval> | null = null;
let clampTimeoutId: ReturnType<typeof setTimeout> | null = null;

const scheduleClamp = () => {
  if (clampTimeoutId) clearTimeout(clampTimeoutId);
  clampTimeoutId = setTimeout(() => {
    clampPreviewImage();
    updateCloseButtonPosition();
  }, 50);
};

const startTracking = () => {
  if (!intervalId) {
    updatePreviewEnhancements();
    intervalId = setInterval(updatePreviewEnhancements, TRACK_INTERVAL_MS);
  }
};

const stopTracking = () => {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
  if (clampTimeoutId) {
    clearTimeout(clampTimeoutId);
    clampTimeoutId = null;
  }
};

export const initAntImagePreviewEnhancements = () => {
  if (typeof window === 'undefined') return;

  const observer = new MutationObserver(() => {
    const previewOpen = document.querySelector('.ant-image-preview-root');
    if (previewOpen) {
      startTracking();
    } else {
      stopTracking();
    }
  });

  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  }

  window.addEventListener('resize', updatePreviewEnhancements);
  window.addEventListener('wheel', scheduleClamp, { passive: true });
  window.addEventListener('mouseup', scheduleClamp);
  window.addEventListener('touchend', scheduleClamp, { passive: true });
};

/** Shared preview config for antd Image components. */
export const sharedImagePreviewConfig = {
  maskClassName: 'prevent-extra-mask-space'
};
